import { Link } from 'react-router-dom';
import './ReviewCard.css';

/**
 * ReviewCard Component
 * Displays a single review with moderation actions
 *
 * Props:
 * - review: Review object from backend API
 * - onApprove: (id) => void - Approve action (no reason needed)
 * - onReject: (id) => void - Opens reject modal (reason required)
 * - onFlag: (id) => void - Opens flag modal (reason required)
 * - onUnpublish: (id) => void - Opens unpublish modal (reason required)
 * - isUpdating: boolean - Disable actions while updating
 */
function ReviewCard({ review, onApprove, onReject, onFlag, onUnpublish, isUpdating }) {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Status config - backend uses UPPERCASE status values
  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { label: 'Pending', className: 'badge-warning' },
      APPROVED: { label: 'Approved', className: 'badge-success' },
      REJECTED: { label: 'Rejected', className: 'badge-danger' },
      FLAGGED: { label: 'Flagged', className: 'badge-orange' },
    };
    const config = statusConfig[status] || statusConfig.PENDING;
    return <span className={`badge ${config.className}`}>{config.label}</span>;
  };

  const renderStars = (rating) => {
    return (
      <div className="star-rating">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill={star <= rating ? '#fbbf24' : '#e5e7eb'}
            className="star-icon"
          >
            <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clipRule="evenodd" />
          </svg>
        ))}
        <span className="rating-text">{rating}/5</span>
      </div>
    );
  };

  // Render action buttons based on review status
  const renderActions = () => {
    const { status, id } = review;

    return (
      <div className="review-actions">
        {/* PENDING: Can approve, reject, or flag */}
        {status === 'PENDING' && (
          <>
            <button
              className="btn btn-success-sm"
              onClick={() => onApprove(id)}
              disabled={isUpdating}
            >
              Approve
            </button>
            <button
              className="btn btn-danger-sm"
              onClick={() => onReject(id)}
              disabled={isUpdating}
            >
              Reject
            </button>
            <button
              className="btn btn-warning-sm"
              onClick={() => onFlag(id)}
              disabled={isUpdating}
            >
              Flag
            </button>
          </>
        )}

        {/* APPROVED: Can unpublish */}
        {status === 'APPROVED' && (
          <button
            className="btn btn-outline-danger"
            onClick={() => onUnpublish(id)}
            disabled={isUpdating}
          >
            Unpublish
          </button>
        )}

        {/* FLAGGED: Can approve or reject after supervisor review */}
        {status === 'FLAGGED' && (
          <>
            <button
              className="btn btn-success-sm"
              onClick={() => onApprove(id)}
              disabled={isUpdating}
            >
              Approve
            </button>
            <button
              className="btn btn-danger-sm"
              onClick={() => onReject(id)}
              disabled={isUpdating}
            >
              Reject
            </button>
          </>
        )}

        {/* REJECTED: Can re-approve to publish again */}
        {status === 'REJECTED' && (
          <button
            className="btn btn-success-sm"
            onClick={() => onApprove(id)}
            disabled={isUpdating}
          >
            Approve
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={`review-card ${review.status.toLowerCase()}`}>
      <div className="review-header">
        <div className="review-meta">
          {renderStars(review.rating)}
          {getStatusBadge(review.status)}
          {review.isVerifiedPurchase && (
            <span className="verified-badge">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path fillRule="evenodd" d="M8.603 3.799A4.49 4.49 0 0112 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 013.498 1.307 4.491 4.491 0 011.307 3.497A4.49 4.49 0 0121.75 12a4.49 4.49 0 01-1.549 3.397 4.491 4.491 0 01-1.307 3.497 4.491 4.491 0 01-3.497 1.307A4.49 4.49 0 0112 21.75a4.49 4.49 0 01-3.397-1.549 4.49 4.49 0 01-3.498-1.306 4.491 4.491 0 01-1.307-3.498A4.49 4.49 0 012.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 011.307-3.497 4.49 4.49 0 013.497-1.307zm7.007 6.387a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
              </svg>
              Verified Purchase
            </span>
          )}
        </div>
        <span className="review-date">{formatDate(review.createdAt)}</span>
      </div>

      <div className="review-product">
        <Link to={`/products/${review.productId}`} className="product-link">
          {review.productName}
        </Link>
        {review.productSlug && (
          <span className="product-slug">/{review.productSlug}</span>
        )}
      </div>

      <div className="review-content">
        {review.title && <h4 className="review-title">{review.title}</h4>}
        <p className="review-text">{review.comment || 'No comment provided.'}</p>
      </div>

      <div className="review-customer">
        <span className="customer-name">{review.userName || 'Anonymous'}</span>
        {review.userEmail && (
          <span className="customer-email">{review.userEmail}</span>
        )}
      </div>

      {/* Show rejection reason for rejected/flagged reviews */}
      {review.rejectionReason && (review.status === 'REJECTED' || review.status === 'FLAGGED') && (
        <div className="rejection-info">
          <div className="rejection-header">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            <span>{review.status === 'FLAGGED' ? 'Flag Reason' : 'Rejection Reason'}</span>
          </div>
          <p className="rejection-reason">{review.rejectionReason}</p>
        </div>
      )}

      {/* Moderation info */}
      {review.moderatedAt && (
        <div className="moderation-info">
          <span>Moderated on {formatDate(review.moderatedAt)}</span>
        </div>
      )}

      <div className="review-footer">
        {renderActions()}
      </div>
    </div>
  );
}

export default ReviewCard;
